import React, { useContext, useEffect, useRef, useState } from "react";
import { Badge, Menu, Skeleton, Typography } from "antd";
import { Link } from "react-router-dom";
import CustomScrollbars from "util/CustomScrollbars";
import SidebarLogo from "./SidebarLogo";
import { THEME_TYPE_LITE } from "../../constants/ThemeSetting";
import IntlMessages from "../../util/IntlMessages";
import { layoutSettingsVar } from "../../apollo/local/cache";
import { useMutation, useQuery, useReactiveVar } from "@apollo/client";
import { AbilityContext } from "../../acl/index";
import { get, startsWith } from "lodash";
import { injectIntl } from "react-intl";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { getAdminPath } from "../../util/router";
import {
   faUsersLine,
   faGauge,
   faBoxArchive,
   faGears,
   faBuildingColumns,
   faRecycle,
   faMailBulk,
   faServer,
   faDumpster,
   faHandHoldingHeart,
   faXmarksLines,
   faMagnifyingGlassChart,
   faBook,
   faSignHanging,
   faProjectDiagram,
   faCalendarDays,
   faHands,
   faIndustry,
   faMugHot,
} from "@fortawesome/free-solid-svg-icons";
import { ADMIN_ROUTE_PREFIX } from "../../constants/App";
import CustomerSelect from "../../components/CustomerSelect";
import getSidebarCustomerItems from "./SidebarCustomerContent";
import { GQL_QUERY_ME } from "../../apollo/query/user";
import { ACLSubject } from "../../constants/Enums";
import { GQL_ADMIN_USER_SET_CURRENT_CUSTOMER } from "../../apollo/mutation/user";
import { AdminContext } from "../App/AdminApp";

const menuItem = (path, icon, labelId) => ({
   key: getAdminPath(path),
   label: (
      <Link to={getAdminPath(path)}>
         <span className="icon">
            <FontAwesomeIcon icon={icon} />
         </span>
         <span>
            <IntlMessages id={labelId} />
         </span>
      </Link>
   ),
});

const SidebarContent = ({ sidebarCollapsed, setSidebarCollapsed, intl }) => {
   const adminContext = useContext(AdminContext);
   const ability = useContext(AbilityContext);

   //assicura first load senza ricaricare la sidebar
   const sidebarLoaded = useRef(null);

   const { data, loading, refetch } = useQuery(GQL_QUERY_ME, {
      onCompleted() {
         if (!sidebarLoaded.current) {
            sidebarLoaded.current = true;
         }
      },
   });

   const [activeCustomerLoading, setActiveCustomerLoading] = useState(false);

   const [updateCurrentCustomer] = useMutation(GQL_ADMIN_USER_SET_CURRENT_CUSTOMER);

   let { themeType } = useReactiveVar(layoutSettingsVar);

   //Selected menu item highlight
   const pathname = window.location.pathname;
   let selectedKey = "";

   const menuKeys = [
      `/${ADMIN_ROUTE_PREFIX}/dashboard`,
      `/${ADMIN_ROUTE_PREFIX}/customers`,
      `/${ADMIN_ROUTE_PREFIX}/tax-providers`,
      `/${ADMIN_ROUTE_PREFIX}/assets`,
      `/${ADMIN_ROUTE_PREFIX}/users`,
      `/${ADMIN_ROUTE_PREFIX}/settings`,
   ];

   menuKeys.forEach((key) => {
      if (pathname === key || startsWith(pathname, key + "/")) {
         selectedKey = key;
         return;
      }
   });
   //

   const me = get(data, "me", null);

   useEffect(() => {
      if (me && me?.manageable_clients_count === 1 && !me.current_client?.id) {
         //se ha accesso ad un cliente ma non è selezionato forza la selezione
         updateCurrentCustomer({ variables: { first: true } }).then(() => {
            refetch();
         });
      }
   }, [me]);

   if (!sidebarLoaded.current && loading) {
      return <Skeleton active={true} />;
   }

   // voci visibili solo se l'utente ha il permesso (ex <Can I=... a=...>)
   const can = (action, subject, items) =>
      ability.can(action, subject) ? items : [];

   const menuItems = [
      ...can("manage", ACLSubject.CLIENT, [
         {
            type: "group",
            key: "mn-group-dashboard",
            label: null,
            children: [menuItem("dashboard", faGauge, "sidebar.dashboard")],
         },
      ]),
      {
         type: "group",
         key: "mn-group-working-area",
         label: (
            <>
               {me.manageable_clients_count > 1 ? (
                  intl.formatMessage({ id: "sidebar.working_area" }).toUpperCase()
               ) : (
                  <Typography.Title level={4} className="gx-mt-3">
                     {me.current_client?.company_name}
                  </Typography.Title>
               )}

               {me.manageable_clients_count > 1 && (
                  <div className="gx-pt-3 gx-pl-4 gx-pr-4">
                     <CustomerSelect
                        value={me?.current_client?.id}
                        style={{ width: "100%" }}
                        onLoading={(value) => {
                           setActiveCustomerLoading(value);
                        }}
                        refreshPageAfterMutation={true}
                     />
                  </div>
               )}

               {activeCustomerLoading && (
                  <Skeleton
                     className="gx-m-4"
                     style={{ maxWidth: "80%" }}
                     active={true}
                  />
               )}
            </>
         ),
         children: activeCustomerLoading
            ? []
            : getSidebarCustomerItems(get(me, "current_client", null)),
      },
      {
         type: "group",
         key: "mn-group-admin",
         label: intl.formatMessage({ id: "sidebar.admin" }).toUpperCase(),
         children: [
            ...can("update", ACLSubject.CLIENT, [
               menuItem("customers", faBuildingColumns, "sidebar.customers"),
            ]),
            ...can("manage", ACLSubject.TAX_PROVIDER, [
               menuItem("tax-providers", faRecycle, "sidebar.tax_providers"),
               menuItem(
                  "privated-tax-providers",
                  faDumpster,
                  "sidebar.private_tax_providers"
               ),
               menuItem("plants", faIndustry, "sidebar.plants"),
            ]),
            ...can("manage", ACLSubject.CHARITY, [
               menuItem("charities", faHandHoldingHeart, "sidebar.charities"),
            ]),
            ...can("manage", ACLSubject.ASSET, [
               menuItem("assets", faBoxArchive, "sidebar.generic_assets"),
            ]),
            ...can("manage", ACLSubject.REPORT, [
               menuItem("reports", faBook, "sidebar.reports"),
            ]),
            ...can("manage", ACLSubject.ESTIMATE, [
               menuItem("estimates", faSignHanging, "sidebar.estimates"),
            ]),
            { type: "divider", key: "divider-sidebar-menu" },
            ...can("manage", ACLSubject.USER, [
               menuItem("users", faUsersLine, "sidebar.users"),
            ]),
            ...can("manage", ACLSubject.TEMPLATE, [
               menuItem("templates", faMailBulk, "sidebar.templates"),
            ]),
            ...can("manage", ACLSubject.SETTING, [
               menuItem("settings", faGears, "sidebar.settings"),
            ]),
         ],
      },
      ...can("manage", ACLSubject.USER || ACLSubject.DATA_ANALYSIS, [
         { type: "divider", key: "divider-sidebar-menu2" },
         menuItem("data-analysis", faMagnifyingGlassChart, "sidebar.data_analysis"),
         {
            key: getAdminPath("activity-monitor"),
            label: (
               <Link to={getAdminPath("activity-monitor")}>
                  <Badge
                     offset={[5, 0]}
                     count={adminContext?.selectedDossierIds.length}
                  >
                     <span className="icon">
                        <FontAwesomeIcon icon={faServer} />
                     </span>
                     <span>
                        <IntlMessages id="dossiers.activity_monitor" />
                     </span>
                  </Badge>
               </Link>
            ),
         },
         menuItem("massive-operations", faMugHot, "sidebar.massive_operations"),
         menuItem("logs", faXmarksLines, "common.logs"),
      ]),
      ...can("update", ACLSubject.CRM_CONTACT, [
         {
            type: "group",
            key: "mn-group-crm",
            label: intl.formatMessage({ id: "sidebar.crm" }).toUpperCase(),
            children: [
               menuItem("crm/calendar", faCalendarDays, "common.calendar"),
               menuItem("crm/contacts", faProjectDiagram, "sidebar.crm_contacts"),
               menuItem("crm/negotiations", faHands, "sidebar.crm_negotiations"),
               menuItem("crm/templates", faMailBulk, "sidebar.templates"),
               menuItem("crm/settings", faGears, "sidebar.settings"),
            ],
         },
      ]),
   ];

   return (
      <>
         <SidebarLogo
            sidebarCollapsed={sidebarCollapsed}
            setSidebarCollapsed={setSidebarCollapsed}
         />

         <div
            className="gx-sidebar-content"
            style={{ height: "calc(100vh - 72px)" }}
         >
            <CustomScrollbars className="gx-layout-sider-scrollbar_todo">
               <Menu
                  selectedKeys={[selectedKey]}
                  theme={themeType === THEME_TYPE_LITE ? "lite" : "dark"}
                  mode="inline"
                  style={{ overflowX: "hidden" }}
                  items={menuItems}
               />
            </CustomScrollbars>
         </div>
      </>
   );
};

SidebarContent.propTypes = {};

export default injectIntl(SidebarContent);
